<template>
    <div class="border border-border bg-white p-4 dark:bg-card">
        <!-- Metric cards + timeframe -->
        <div class="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-start">
            <div class="flex w-full flex-wrap gap-2">
                <!-- Active users -->
                <div
                    class="flex w-full flex-col gap-4 bg-new-topol-blue px-3 pb-4 pt-3 text-white shadow-sm sm:w-[250px]"
                >
                    <p class="text-xs">Active users</p>
                    <div class="flex flex-col gap-2">
                        <p class="font-display text-[40px] font-medium leading-none">
                            {{ activeUsage.activeUsers }}
                        </p>
                        <p class="text-[10px] text-white/80">{{ monthLabel }}</p>
                    </div>
                </div>

                <!-- New users -->
                <div
                    class="flex w-full flex-col gap-4 bg-success px-3 pb-4 pt-3 text-white shadow-sm sm:w-[250px]"
                >
                    <p class="text-xs">New users</p>
                    <div class="flex flex-col gap-2">
                        <p class="font-display text-[40px] font-medium leading-none">
                            {{ activeUsage.newUsers }}
                        </p>
                        <p class="text-[10px] text-white/80">{{ monthLabel }}</p>
                    </div>
                </div>

                <!-- Usage (data) -->
                <div
                    class="flex w-full flex-col gap-4 px-3 pb-4 pt-3 text-body-muted sm:w-[250px]"
                >
                    <p class="text-xs">Usage</p>
                    <div class="flex flex-col gap-2">
                        <div class="flex items-end gap-1">
                            <p class="font-display text-[40px] font-medium leading-none">
                                {{ usageData.value }}
                            </p>
                            <p class="font-display text-[28px] font-medium leading-[1.05]">
                                {{ usageData.unit }}
                            </p>
                        </div>
                        <p class="text-[10px]">{{ monthLabel }}</p>
                    </div>
                </div>
            </div>

            <!-- Timeframe -->
            <DropdownMenu :modal="false">
                <DropdownMenuTrigger as-child>
                    <button
                        type="button"
                        class="flex h-10 shrink-0 items-center gap-1 border border-border bg-surface-card px-4 py-2 text-sm text-foreground transition-colors hover:bg-[#eceef1] dark:bg-muted"
                    >
                        {{ monthLabel }}
                        <ChevronDown class="size-4 text-muted-foreground" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        v-for="entry in monthOptions"
                        :key="entry.key"
                        class="text-sm"
                        :class="{ 'font-medium': entry.key === activeMonth }"
                        @click="activeMonth = entry.key"
                    >
                        <Check v-if="entry.key === activeMonth" class="size-3.5" />
                        <span v-else class="size-3.5" />
                        {{ entry.label }}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>

        <!-- Data traffic chart -->
        <div class="mt-2">
            <div
                v-if="activeMonthTraffic.length > 0"
                class="relative h-[400px] w-full bg-surface-card p-4 dark:bg-muted"
            >
                <canvas ref="chartCanvas"></canvas>
            </div>
            <div
                v-else
                class="flex h-[400px] w-full items-center justify-center bg-surface-card dark:bg-muted"
            >
                <p class="text-xs text-muted-foreground">
                    No data traffic for this month yet.
                </p>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, nextTick, PropType } from 'vue';
import Chart from 'chart.js/auto';
import { ChevronDown, Check } from 'lucide-vue-next';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface MonthlyUsage {
    key: string;
    label: string;
    activeUsers: number;
    newUsers: number;
    dataUsage: number;
}

const props = defineProps({
    usageByMonth: {
        type: Object as PropType<Record<string, MonthlyUsage>>,
        default: () => ({}),
    },
    dataTraffic: {
        type: Array as PropType<Array<{ day: string; bytes: number; month: string }>>,
        default: () => [],
    },
});

const monthOptions = computed<MonthlyUsage[]>(() =>
    Object.values(props.usageByMonth).sort((a, b) => (a.key < b.key ? 1 : -1)),
);

const defaultMonth = computed(() => monthOptions.value[0]?.key ?? '');
const activeMonth = ref<string>(defaultMonth.value);

watch(defaultMonth, (val) => {
    if (!activeMonth.value && val) activeMonth.value = val;
});

const activeUsage = computed<MonthlyUsage>(
    () =>
        props.usageByMonth[activeMonth.value] ?? {
            key: '',
            label: '',
            activeUsers: 0,
            newUsers: 0,
            dataUsage: 0,
        },
);

const monthLabel = computed(() => activeUsage.value.label || 'This month');

const splitBytes = (bytes: number): { value: string; unit: string } => {
    if (!bytes) return { value: '0', unit: 'B' };
    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return {
        value: String(parseFloat((bytes / Math.pow(k, i)).toFixed(0))),
        unit: sizes[i],
    };
};

const usageData = computed(() => splitBytes(activeUsage.value.dataUsage));

const activeMonthTraffic = computed(() =>
    (props.dataTraffic || []).filter((d) => d.month === activeMonth.value),
);

const formatBytesWithUnit = (bytes: number, decimals = 0): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
};

const chartCanvas = ref<HTMLCanvasElement>();
let chartInstance: Chart | null = null;

const renderChart = () => {
    if (!chartCanvas.value || activeMonthTraffic.value.length === 0) return;

    if (chartInstance) {
        chartInstance.destroy();
    }

    chartInstance = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
            labels: activeMonthTraffic.value.map((d) => d.day),
            datasets: [
                {
                    data: activeMonthTraffic.value.map((d) => d.bytes),
                    backgroundColor: '#0048ff',
                    barPercentage: 1,
                    categoryPercentage: 0.6,
                },
            ],
        },
        options: {
            maintainAspectRatio: false,
            interaction: { intersect: true, mode: 'index' },
            scales: {
                y: {
                    grid: { color: '#e6e8eb' },
                    ticks: {
                        callback: (value) =>
                            value !== 0
                                ? formatBytesWithUnit(parseInt(value.toString()), 0)
                                : undefined,
                        maxTicksLimit: 5,
                        color: '#6B7280',
                        font: { size: 10 },
                    },
                },
                x: {
                    grid: { display: false },
                    ticks: { color: '#6B7280', font: { size: 10 } },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#252F3F',
                    caretSize: 0,
                    cornerRadius: 6,
                    padding: 6,
                    bodyFont: { size: 12 },
                    callbacks: {
                        label: (ctx) =>
                            formatBytesWithUnit(parseInt(ctx.raw?.toString() || '0'), 2),
                        title: () => '',
                    },
                },
            },
        },
    });
};

watch(
    activeMonthTraffic,
    async () => {
        await nextTick();
        renderChart();
    },
    { deep: true },
);

watch(activeMonth, async () => {
    await nextTick();
    renderChart();
});

onMounted(() => {
    if (activeMonthTraffic.value.length > 0) {
        renderChart();
    }
});
</script>
